import { getCodeFromErrors, getExtensionFromErrors } from '@common/components';
import type { ApolloError } from '@common/hooks';
import type {
  LoadBalancingScheduleEmploymentType,
  LoadBalancingScheduleShiftPreference,
  LoadBalancingScheduleShiftSource,
  LoadBalancingScheduleWeekStatus,
  LoadBalancingShiftPatternPreference,
} from '@common/graphql';
import type { DragDropOutcomeType } from '@load-balancing/services';
import type {
  ScheduleReviewDemandType,
  ScheduleReviewShiftType,
  ScheduleReviewTeamMemberType,
} from '@load-balancing/graphql/queries/schedule-review';
import type {
  ScheduleDemandType,
  ScheduleEmploymentType,
  ScheduleShiftSourceType,
  ScheduleShiftType,
  ScheduleShiftTypeSectionKeyType,
  ScheduleTeamMemberType,
  ScheduleTeamShiftPatternType,
  ScheduleTeamShiftType,
  ScheduleWeekStatusType,
} from '@load-balancing/types';

// After a manual move the recompute-on-read feeds must refresh against the mutated output
// snapshot: the grid (`ScheduleReview`) and the health strip's own query (it lives in a child
// component) always, plus only the candidate feed actually active for that action — assign
// operates on an open cell (`OpenShiftCandidates`), unassign on a real shift (`ShiftCandidates`).
// Refetching the inactive feed would re-issue it with empty variables (WORK-6384). Shared by the
// Shift Detail panel mutations (page) and the drag mutations (drag-drop hook).
export const REFETCH_AFTER_ASSIGN = [
  'ScheduleReview',
  'ScheduleHealthSnapshot',
  'OpenShiftCandidates',
];
export const REFETCH_AFTER_UNASSIGN = [
  'ScheduleReview',
  'ScheduleHealthSnapshot',
  'ShiftCandidates',
];

// Reconcile the backend's UPPER_CASE GraphQL enums to the FE's lower-case unions.
// An exhaustive Record (not `.toLowerCase() as`) makes a new backend enum value from
// `yarn codegen` a compile error here, rather than a silently-dropped runtime value.
export const WEEK_STATUS_BY_BACKEND: Record<
  LoadBalancingScheduleWeekStatus,
  ScheduleWeekStatusType['status']
> = {
  COMPLETE: 'complete',
  EMPTY: 'empty',
  PENDING: 'pending',
};

const SHIFT_SOURCE_BY_BACKEND: Record<
  LoadBalancingScheduleShiftSource,
  ScheduleShiftSourceType
> = {
  BALANCER: 'balancer',
  FLOAT: 'float',
  MANUAL: 'manual',
  OPEN: 'open',
  PATTERN: 'pattern',
  PTO: 'pto',
  SELF: 'self',
};

const EMPLOYMENT_BY_BACKEND: Record<
  LoadBalancingScheduleEmploymentType,
  ScheduleEmploymentType
> = {
  AGENCY: 'agency',
  CORE: 'core',
};

const SHIFT_PREFERENCE_BY_BACKEND: Record<
  LoadBalancingScheduleShiftPreference,
  ScheduleTeamShiftType
> = {
  DAY: 'day',
  EITHER: 'either',
  NIGHT: 'night',
};

const SHIFT_PATTERN_BY_BACKEND: Record<
  LoadBalancingShiftPatternPreference,
  ScheduleTeamShiftPatternType
> = {
  CONSECUTIVE: 'consecutive',
  NON_CONSECUTIVE: 'non_consecutive',
  NO_PREFERENCE: 'no_preference',
};

// Maps a GraphQL shift onto the calendar's domain shape. The UPPER_CASE GraphQL
// `source` enum maps to the lower-case FE union via SHIFT_SOURCE_BY_BACKEND; the
// `shiftTypeSection` string narrows to the FE section key; nullable fields collapse
// to `undefined`.
export function toShift(shift: ScheduleReviewShiftType): ScheduleShiftType {
  return {
    id: shift.id,
    dayKey: shift.dayKey,
    shiftTypeSection: shift.shiftTypeSection as ScheduleShiftTypeSectionKeyType,
    role: shift.role,
    isChargeRole: shift.isChargeRole,
    startHour: shift.startHour,
    startTime: shift.startTime ?? undefined,
    endTime: shift.endTime ?? undefined,
    workerName: shift.workerName ?? undefined,
    assignedWorkerId: shift.assignedWorkerId ?? undefined,
    source: SHIFT_SOURCE_BY_BACKEND[shift.source],
    suggested: shift.suggested ?? undefined,
    fixedCommitment: shift.fixedCommitment ?? undefined,
    // Non-null list server-side; normalize each reason's nullable weight to `number | null`.
    assignedReasons: shift.assignedReasons.map((reason) => ({
      key: reason.key,
      label: reason.label,
      hard: reason.hard,
      weight: reason.weight ?? null,
    })),
  };
}

// Maps a GraphQL demand cell onto the calendar's domain shape. The open
// `shiftTypeSection` string narrows to the FE section key, mirroring toShift.
export function toDemand(demand: ScheduleReviewDemandType): ScheduleDemandType {
  return {
    dayKey: demand.dayKey,
    shiftTypeSection:
      demand.shiftTypeSection as ScheduleShiftTypeSectionKeyType,
    role: demand.role,
    plannedCount: demand.plannedCount,
  };
}

// Maps a GraphQL team member onto the FE domain shape. UPPER_CASE enums map to the
// lower-case FE unions via the exhaustive Records; nullable metric fields pass through
// (the helpers/panel render "—" until later changes populate them).
export function toTeamMember(
  member: ScheduleReviewTeamMemberType
): ScheduleTeamMemberType {
  return {
    id: member.id,
    name: member.name,
    role: member.role,
    unit: member.unit,
    employmentType: EMPLOYMENT_BY_BACKEND[member.employmentType],
    shiftType: SHIFT_PREFERENCE_BY_BACKEND[member.shiftType],
    shiftPattern: SHIFT_PATTERN_BY_BACKEND[member.shiftPattern],
    chargeEligible: member.chargeEligible,
    // Per-slot charge-eligibility history (WORK-6528): the drag affordance picks the term covering an
    // open slot's date. Pass the dates straight through (already ISO8601 strings); the flat
    // chargeEligible above still drives the Team-tab display column.
    terms: member.terms.map((term) => ({
      effectiveStartDate: term.effectiveStartDate,
      effectiveEndDate: term.effectiveEndDate ?? null,
      role: term.role ?? null,
      chargeEligible: term.chargeEligible,
      active: term.active,
      homeUnitMember: term.homeUnitMember,
    })),
    homeUnitMember: member.homeUnitMember,
    // Codegen types nullable fields as optional; normalize undefined → null so the
    // FE keys stay uniformly `number | null` (the helpers/panel branch on `== null`).
    fteHours: member.fteHours ?? null,
    scheduledHours: member.scheduledHours ?? null,
    remainingHours: member.remainingHours ?? null,
    active: member.active,
    weekendCount: member.weekendCount,
    weekendTarget: member.weekendTarget ?? null,
    weekendWillingness: member.weekendWillingness ?? null,
  };
}

/**
 * Open-slot coverage base for a shift's cell: the planned demand (target) and the count of
 * already-filled (non-open) shifts in the same day/role/band. The panel adds `after`
 * (filled + 1) per candidate. Null when the cell has no demand row.
 */
export function cellCoverage(
  shift: ScheduleShiftType,
  shifts: ScheduleShiftType[],
  demands: ScheduleDemandType[]
): { before: number; target: number } | null {
  const sameCell = (cell: {
    dayKey: string;
    role: string;
    shiftTypeSection: ScheduleShiftTypeSectionKeyType;
  }): boolean =>
    cell.dayKey === shift.dayKey &&
    cell.role === shift.role &&
    cell.shiftTypeSection === shift.shiftTypeSection;

  const demand = demands.find(sameCell);
  if (demand === undefined) return null;

  const before = shifts.filter(
    (entry) => sameCell(entry) && entry.source !== 'open'
  ).length;
  return { before, target: demand.plannedCount };
}

/**
 * The grid-cell descriptor encoded in a synthesized open-row id, or null for any
 * other shift id. Open rows carry no backing shift uuid; OpenShiftSynthesizer joins
 * the cell coordinates as `open:<week>:<day>:<band>:<role>:<index>`, and the open
 * candidate query addresses the cell by those coordinates (the index is dropped —
 * every open row in a cell shares the same demand). Requires the exact 6-part shape
 * so a real uuid (or a future id format) falls through to the uuid candidate path.
 */
export function parseOpenShiftId(shiftId: string | null): {
  weekKey: string;
  dayKey: string;
  shiftTypeSection: string;
  role: string;
} | null {
  if (shiftId === null) return null;

  const parts = shiftId.split(':');
  if (parts.length !== 6 || parts[0] !== 'open') return null;

  return {
    weekKey: parts[1],
    dayKey: parts[2],
    shiftTypeSection: parts[3],
    role: parts[4],
  };
}

// Copy for the single primary blocking constraint the backend names (the `constraintKey` extension
// on a WORKER_NOT_ELIGIBLE error). Keys mirror LoadBalancing::Constraints#constraint_key plus the
// two synthetic term verdicts (no_effective_term / worker_inactive). PROPOSED copy pending
// product; an unmapped or absent key falls back to ASSIGN_ELIGIBILITY_FALLBACK.
const ASSIGN_CONSTRAINT_COPY: Record<string, string> = {
  shift_overlap: 'Worker already has an overlapping shift.',
  role_ineligible: "Worker isn't eligible for this role.",
  charge_moonlight_unit_mismatch:
    'A charge shift can only be assigned to a worker from its home unit here — drag to assign across units.',
  no_effective_term: 'Worker has no active contract on that date.',
  worker_inactive: 'Worker is inactive on that date.',
  fte_target_violation: "This would exceed the worker's weekly FTE target.",
  max_daily_hours: "This would exceed the worker's daily-hours limit.",
  max_consecutive_hours:
    "This would exceed the worker's consecutive-hours limit.",
  preferred_shift_time:
    "This shift falls outside the worker's available times.",
  fte_contract_required: 'Worker has no FTE contract covering this assignment.',
  unit_not_allowed: "Worker isn't permitted to work this unit.",
};

const ASSIGN_ELIGIBILITY_FALLBACK =
  'This worker is no longer eligible for this slot. Refresh to see the latest candidates.';

// Maps an assignShift failure to user-facing copy. The backend (assign_shift.rb) raises each
// failure as a top-level GraphQL error carrying `extensions.code`. CONFLICT (advisory-lock
// contention) and NOT_FOUND (vanished or already-filled slot) both mean "the schedule moved
// under you" — tell the user to refresh, not blindly retry. WORKER_NOT_ELIGIBLE additionally
// carries the primary `constraintKey`, which names why; the UNPROCESSABLE_ENTITY catch-all and an
// absent/unknown key fall back to generic copy.
export function assignErrorMessage(error: ApolloError): string {
  switch (getCodeFromErrors(error.graphQLErrors)) {
    case 'WORKER_NOT_ELIGIBLE': {
      const key = getExtensionFromErrors(error.graphQLErrors, 'constraintKey');
      return (
        (key && ASSIGN_CONSTRAINT_COPY[key]) || ASSIGN_ELIGIBILITY_FALLBACK
      );
    }
    case 'CONFLICT':
      return 'This schedule changed while you were editing. Refresh and try again.';
    case 'NOT_FOUND':
      return 'This open slot is no longer available. Refresh to see the latest schedule.';
    default:
      return 'Could not assign this worker. Please try again.';
  }
}

// Maps an unassignShift failure to user-facing copy. Same code contract as assign, minus the
// eligibility case (unassign has no eligibility gate): CONFLICT signals a moved schedule (refresh).
// NOT_FOUND splits on the `reason` extension: 'no_balancer_worker' means the row is present but has
// no balancer worker to remove (refresh will NOT help — don't say "refresh"); 'shift_missing' or an
// absent reason means a stale id (refresh fixes it). Everything else falls back to generic copy.
export function unassignErrorMessage(error: ApolloError): string {
  switch (getCodeFromErrors(error.graphQLErrors)) {
    case 'CONFLICT':
      return 'This schedule changed while you were editing. Refresh and try again.';
    case 'NOT_FOUND':
      if (
        getExtensionFromErrors(error.graphQLErrors, 'reason') ===
        'no_balancer_worker'
      ) {
        return "This shift isn't managed by the balancer and can't be unassigned here.";
      }
      return 'This shift is no longer available. Refresh to see the latest schedule.';
    default:
      return 'Could not unassign this worker. Please try again.';
  }
}

// Copy for a drag MOVE whose unassign leg committed but whose assign leg then failed. The worker
// is correctly left unassigned (the move sequences unassign-first, so it is never double-booked),
// so the generic assign error ("Could not assign this worker") would wrongly imply nothing happened
// — name the partial outcome explicitly instead.
export const MOVE_ASSIGN_LEG_ERROR =
  'Worker was unassigned but could not be moved to the new slot. Refresh to review.';

// Maps a drag-drop outcome (from `runDragDrop`) to the single toast the drag-drop hook raises, or
// null for a noop (nothing happened — no toast). Co-locating the copy here keeps `runDragDrop` free
// of presentation deps while a MOVE still surfaces one "Worker moved." message rather than the two
// its legs would otherwise fire. Errors resolve the per-code copy; the partial move (`move-assign-
// error`) uses MOVE_ASSIGN_LEG_ERROR, which names the "unassigned, not moved" outcome explicitly.
export function dragDropToast(
  outcome: DragDropOutcomeType
): { kind: 'success' | 'error'; message: string } | null {
  switch (outcome.kind) {
    case 'assigned':
      return { kind: 'success', message: 'Worker assigned.' };
    case 'moved':
      return { kind: 'success', message: 'Worker moved.' };
    case 'assign-error':
      return { kind: 'error', message: assignErrorMessage(outcome.error) };
    case 'unassign-error':
      return { kind: 'error', message: unassignErrorMessage(outcome.error) };
    case 'move-assign-error':
      return { kind: 'error', message: MOVE_ASSIGN_LEG_ERROR };
    case 'noop':
      return null;
  }
}

/** Week key whose shift list contains the given shift id, or null if none does. */
export function findShiftWeekKey(
  shiftId: string,
  shiftsByWeek: Record<string, ScheduleShiftType[]>
): string | null {
  const entry = Object.entries(shiftsByWeek).find(([, weekShifts]) =>
    weekShifts.some((shift) => shift.id === shiftId)
  );

  return entry?.[0] ?? null;
}

/**
 * Which week the "Assigned shifts" toggle should snap to, or null to stay on the current week.
 * Returns null when the current week already shows one of the worker's assigned shifts (no
 * unnecessary navigation); otherwise the earliest week that has one, so the fills are always
 * visible regardless of the week on screen. Mirrors the eligible-shift toggle. See WORK-6551.
 * `assignedShiftIds` must be in week-then-in-week order (as getAssignedShiftIdsForMember returns).
 */
export function assignedHighlightWeekKey(
  assignedShiftIds: string[],
  shiftsByWeek: Record<string, ScheduleShiftType[]>,
  currentWeekKey: string | undefined
): string | null {
  if (assignedShiftIds.length === 0) return null;

  const assignedIds = new Set(assignedShiftIds);
  const currentWeekHasAssigned = (
    shiftsByWeek[currentWeekKey ?? ''] ?? []
  ).some((shift) => assignedIds.has(shift.id));
  if (currentWeekHasAssigned) return null;

  return findShiftWeekKey(assignedShiftIds[0], shiftsByWeek);
}
